import { useHandler } from "@redotech/react-util/hook";
import { PurchaseChannel } from "@redotech/redo-model/warranties";
import { useContext, useMemo, useState } from "react";
import { SettingsContext } from "../contexts/settings";

type PurchaseChannelOption = {
  label: string;
  id: string;
  purchaseChannel: PurchaseChannel;
};

const OTHER_OPTION: PurchaseChannelOption = {
  label: "Other",
  id: "other",
  purchaseChannel: PurchaseChannel.RETAIL,
};

const DEFAULT_PURCHASE_CHANNEL_OPTIONS: PurchaseChannelOption[] = [
  {
    label: "Shopify store",
    id: "shopify-store",
    purchaseChannel: PurchaseChannel.ONLINE,
  },
  OTHER_OPTION,
];

function getPurchaseChannelLabel(
  option?: PurchaseChannelOption,
  otherText?: string,
): string {
  if (!option) {
    return "Unknown";
  }
  if (option.id === "other") {
    return `Other (${otherText ?? "Unknown"})`;
  }
  return option.label;
}

export function useRegister({
  toRetailRegistration,
  toOnlineRegistration,
}: {
  toRetailRegistration: (channelName: string) => void;
  toOnlineRegistration: (channelName: string) => void;
}) {
  const settings = useContext(SettingsContext);
  const [selectedPurchaseOptionId, setSelectedPurchaseOptionId] = useState<
    string | undefined
  >(undefined);
  const [otherPurchaseChannel, setOtherPurchaseChannel] = useState<string>("");

  const purchaseChannelOptions: PurchaseChannelOption[] = useMemo(() => {
    const purchaseChannels = settings?.warranties?.purchaseChannels;
    if (!purchaseChannels || purchaseChannels.length === 0) {
      return DEFAULT_PURCHASE_CHANNEL_OPTIONS;
    }
    const options = purchaseChannels.map((channel, index) => ({
      label: channel.displayName,
      id: `purchase-channel-${index}`,
      purchaseChannel: channel.purchaseChannel,
    }));
    if (settings?.warranties?.showOtherOption) {
      options.push(OTHER_OPTION);
    }
    return options;
  }, [settings?.warranties]);

  const selectedPurchaseOption = useMemo(() => {
    return purchaseChannelOptions.find(
      (option) => option.id === selectedPurchaseOptionId,
    );
  }, [purchaseChannelOptions, selectedPurchaseOptionId]);

  const handleContinue = useHandler(() => {
    const label = getPurchaseChannelLabel(
      selectedPurchaseOption,
      otherPurchaseChannel,
    );
    if (selectedPurchaseOption?.purchaseChannel === PurchaseChannel.ONLINE) {
      toOnlineRegistration(label);
    } else {
      toRetailRegistration(label);
    }
  });

  const continueDisabled =
    !selectedPurchaseOptionId ||
    (selectedPurchaseOptionId === OTHER_OPTION.id && !otherPurchaseChannel);

  return {
    texts: {
      merchantName: settings?.merchantName || "Unknown Merchant",
      title: "Register your product",
      subtitle: "Where did you purchase your product?",
      continueButtonText: "Continue",
    },
    continueDisabled,
    selectedPurchaseOptionId,
    setSelectedPurchaseOptionId,
    purchaseChannelOptions,
    isOtherOptionSelected: selectedPurchaseOptionId === OTHER_OPTION.id,
    otherPurchaseChannel,
    setOtherPurchaseChannel,
    onContinue: handleContinue,
  };
}
